import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Send, Lock, PlayCircle, FileText, BookOpen, ChevronDown, ChevronUp, RefreshCw, Sparkles, Leaf, Info, Star, X, ChevronRight, ShieldCheck, Zap, Stethoscope, Utensils, Flower2, HelpCircle, AlertCircle, Mic, Volume2, Bookmark, BookmarkPlus, Save, Check, MessageSquare, Menu, BookMarked, LogOut, UserCircle, History, TreePine, Sprout, ShieldAlert, HeartPulse, Target as TargetIcon, Brain } from 'lucide-react';
import { Message, QueryUsage, RemedyDocument, RecommendationMetadata, AppView, SubscriptionStatus, YogaPose, Meal, User, UserProfile, FeatureContext } from '../types';
import { SYSTEM_INSTRUCTION, MAX_PROMPT_LENGTH, DAILY_QUERY_LIMIT } from '../utils/constants';
import { searchVectorDatabase, logAnalyticsEvent, saveRemedy, getCurrentUser, getUserLibrary, forceSignOut, warmupDatabase, checkDailyQueryLimit, getUserSearchHistory, checkSubscriptionStatus, setupAuthListener, fetchUserRecord, fetchUserProfile, supabase, validateSession } from '../services/backendService';
import { Logo } from '../components/Logo';
import { playRawAudio } from '../utils/audio';
import AuthForm from '../components/AuthForm';
import SubscriptionModal from '../components/SubscriptionModal';
import AccountSettings from '../components/AccountSettings';
import Library from '../components/Library';
import YogaAid from '../components/YogaStudio';
import NutriHeal from '../components/DietKitchen';
import BotanicalRx from '../components/BotanicalRx';
import { LegalNotice } from '../components/LegalNotice';
import { AboutView } from '../components/AboutView';
import { FAQView } from '../components/FAQView';
import { VoiceConsultation } from '../components/VoiceConsultation';
import ChatInterface from '../components/ChatInterface';
import WellnessPassport from '../components/WellnessPassport';
import ManasAid from './ManasAid';
import logger from "@/utils/logger";

const ConsultationApp: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState<User | null>(getCurrentUser());
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [currentView, setCurrentView] = useState<AppView>(AppView.CHAT);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);
    const authInitialized = useRef(false);

    const [chatMessages, setChatMessages] = useState<Message[]>([
        {
            id: 'welcome',
            role: 'model',
            content: (user && userProfile && (userProfile.age || userProfile.sex))
                ? `Namaste, ${user.name || 'Friend'}. I see we have your wellness profile. How is your health today?`
                : 'Namaste. I am Nature Nani. Tell me what ailments you are experiencing. To give you the best wisdom from our scrolls, tell me your age, sex and any past medical history or other health concerns I should know about',
            timestamp: Date.now()
        }
    ]);

    const [featureContext, setFeatureContext] = useState<FeatureContext | null>(null);
    const [subscriptionState, setSubscriptionState] = useState({ hasAccess: false, daysRemaining: 0, isTrialExpired: false, status: 'free' as SubscriptionStatus });
    const [showPaywall, setShowPaywall] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [searchHistory, setSearchHistory] = useState<string[]>([]);
    const [triggerQuery, setTriggerQuery] = useState<string>('');
    const [isVoiceTrigger, setIsVoiceTrigger] = useState(false);
    const [queryUsage, setQueryUsage] = useState<QueryUsage>({ count: 0, limit: DAILY_QUERY_LIMIT, remaining: DAILY_QUERY_LIMIT, isUnlimited: false });
    const [returnToChat, setReturnToChat] = useState(false);
    
    // Consultation state for module integration
    const [activeConsultationResponse, setActiveConsultationResponse] = useState<string | null>(null);
    const [activeAilmentId, setActiveAilmentId] = useState<string | null>(null);
    const [showVikritiNudge, setShowVikritiNudge] = useState(false);

    // Session-level caches
    const mealPlanCache = useRef<Map<string, Meal[]>>(new Map());
    const yogaProtocolCache = useRef<Map<string, any>>(new Map());

    const refreshAppData = useCallback(async (u: User) => {
        try {
            const freshUser = await fetchUserRecord(u.email);
            const activeUser = freshUser || u;
            if (freshUser) setUser(freshUser);

            const [usage, history, subStatus, profile] = await Promise.all([
                checkDailyQueryLimit(activeUser),
                getUserSearchHistory(activeUser),
                checkSubscriptionStatus(activeUser),
                fetchUserProfile(activeUser.id)
            ]);

            setQueryUsage(usage);
            setSearchHistory(history);
            setSubscriptionState({
                hasAccess: subStatus.hasAccess,
                daysRemaining: subStatus.daysRemaining,
                isTrialExpired: subStatus.isTrialExpired,
                status: subStatus.status as SubscriptionStatus
            });
            if (profile) setUserProfile(profile);

            // Check Onboarding Status
            if (activeUser?.id) {
                const { data: journey } = await supabase
                    .from('user_journey')
                    .select('onboarding_completed')
                    .eq('user_id', activeUser.id)
                    .maybeSingle();

                logger.log("[App] Journey status check:", { 
                    userId: activeUser.id, 
                    completed: journey?.onboarding_completed,
                    raw: journey 
                });

                if (!journey?.onboarding_completed) {
                    // If user explicitly skipped in this session (passed via checking location state), don't redirect
                    // @ts-ignore
                    if (location.state?.skippedOnboarding) {
                        setShowOnboardingBanner(true);
                    } else {
                        logger.log("[App] Redirecting to onboarding...");
                        navigate('/onboarding');
                    }
                } else {
                    setShowOnboardingBanner(false);
                }
            }
        } catch (err) {
            logger.error("[App] Data refresh failed:", err);
        }
    }, []);

    const handleAuthChange = useCallback((u: User | null) => {
        if (u) {
            setUser(u);
            setShowAuthModal(false);
            refreshAppData(u);
        } else {
            setUser(null);
            setSearchHistory([]);
            setQueryUsage({ count: 0, limit: DAILY_QUERY_LIMIT, remaining: DAILY_QUERY_LIMIT, isUnlimited: false });
            setSubscriptionState({ hasAccess: false, daysRemaining: 0, isTrialExpired: false, status: 'free' as SubscriptionStatus });
            setCurrentView(AppView.CHAT);
            
            // Clear session caches on logout
            mealPlanCache.current.clear();
            yogaProtocolCache.current.clear();
            setActiveConsultationResponse(null);
            setActiveAilmentId(null);
        }
    }, [refreshAppData]);

    const handleLogout = async () => {
        setIsMobileMenuOpen(false);
        handleAuthChange(null);
        await forceSignOut();
    };

    useEffect(() => {
        if (!authInitialized.current) {
            authInitialized.current = true;
            warmupDatabase();
            const u = getCurrentUser();
            if (u) refreshAppData(u);
            setupAuthListener(handleAuthChange);
        }

        // Handle navigation state from onboarding (Direct View Switching)
        if (location.state?.view === 'profile') {
            setCurrentView(AppView.PROFILE);
            // Clear state to avoid re-triggering on future refreshes
            window.history.replaceState({}, document.title);
        }

        const handleFocus = async () => {
            const isValid = await validateSession();
            if (!isValid && user) {
                // If session is invalid and we thought we had a user, try to refresh or logout
                // validateSession attempts refresh internally. If it fails, we should logout.
                logger.log("[App] Session invalid on focus, logging out...");
                handleLogout();
            } else if (isValid && user) {
                // Aggressively re-fetch to ensure premium status didn't change while away
                import('../services/backendService').then(m => {
                    m.getCurrentUser(); // ensures module loaded
                    // We don't export getOrCreateUser by default, so we'll rely on fetchUserRecord which refreshAppData does internally.
                    // But to explicitly meet Task 4, we await refreshAppData which calls fetchUserRecord and updates subscription states.
                });
                refreshAppData(user);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [handleAuthChange, refreshAppData, user]);

    // Smart Vikriti Nudge Logic
    useEffect(() => {
        if (!userProfile || showVikritiNudge) return;

        // Only nudge if they are on a Wellness Journey and haven't done a Vikriti assessment
        if (userProfile.user_intent === 'wellness' && !userProfile.vikriti_dosha) {
            const lastMessage = chatMessages[chatMessages.length - 1];
            if (lastMessage?.role === 'user') {
                const keywords = ["pain", "ache", "digestion", "bloat", "sleep", "anxiety", "skin", "rash", "fever", "cough", "cold", "allergy", "infection", "headache", "migraine", "constipation", "stomach", "joint", "muscle", "stress", "tired", "energy"];
                const hasKeyword = keywords.some(k => lastMessage.content.toLowerCase().includes(k));
                if (hasKeyword) {
                    setShowVikritiNudge(true);
                    
                    // Also add the in-chat note once
                    setChatMessages(prev => [...prev, {
                        id: `nudge-${Date.now()}`,
                        role: 'model',
                        content: "My dear, knowing your current state of balance would help me give you even more precise guidance. You can add a health concern in your profile — it takes just a few minutes.",
                        timestamp: Date.now()
                    }]);
                }
            }
        }
    }, [chatMessages, userProfile, showVikritiNudge]);

    const handleNav = (view: AppView, isPremiumFeature = false) => {
        if (isPremiumFeature && !subscriptionState.hasAccess) {
            setShowPaywall(true);
            return;
        }
        if (!user && (view === AppView.ACCOUNT || view === AppView.LIBRARY || view === AppView.VOICE)) {
            setShowAuthModal(true);
            return;
        }
        setCurrentView(view);
        setFeatureContext(null);
        setIsMobileMenuOpen(false);
        setShowAuthModal(false);
        setReturnToChat(false);
    };

    const handleFeatureHandoff = (view: AppView, context: FeatureContext, fromChat = false) => {
        const isPremiumView = [AppView.YOGA, AppView.DIET, AppView.LIBRARY, AppView.BOTANICAL, AppView.WISDOM].includes(view);
        if (isPremiumView && !subscriptionState.hasAccess) {
            setShowPaywall(true);
            return;
        }
        setFeatureContext(context);
        setCurrentView(view);
        setIsMobileMenuOpen(false);
        setReturnToChat(fromChat);
    };

    const handleVoiceConsult = (query: string) => {
        setTriggerQuery(query);
        setIsVoiceTrigger(true);
        setCurrentView(AppView.CHAT);
    };

    return (
        <div className="h-screen w-screen flex flex-col md:flex-row overflow-hidden bg-sage-50">
            <div className="md:hidden flex items-center justify-between p-4 bg-white border-b border-sage-200 z-30">
                <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-sage-700 hover:bg-sage-50 rounded-lg transition-colors"><Menu size={24} /></button>
                <Link to="/">
                    <Logo className="h-8 w-8" textClassName="text-xl" showSlogan={false} />
                </Link>
                <div className="w-10"></div>
            </div>

            <div className={`fixed md:relative inset-y-0 left-0 z-50 w-72 md:w-64 bg-white border-r border-sage-200 p-4 flex flex-col justify-between shadow-xl md:shadow-none transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                    <div className="mb-6 px-2 flex items-center justify-between">
                        <Link to="/">
                            <Logo className="h-10 w-10" textClassName="text-2xl" showSlogan={false} />
                        </Link>
                        <button onClick={() => setIsMobileMenuOpen(false)} className="md:hidden p-2 text-gray-400 hover:text-sage-600 transition-colors"><X size={20} /></button>
                    </div>

                    <div className="space-y-2">
                        <button onClick={() => handleNav(AppView.CHAT)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center gap-3 transition-colors ${currentView === AppView.CHAT ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><MessageSquare size={18} /> Consultation</button>
                        <button onClick={() => handleNav(AppView.VOICE, false)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center justify-between transition-colors group ${currentView === AppView.VOICE ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><div className="flex items-center gap-3"><Mic size={18} className="text-sage-600 group-hover:animate-pulse" /> Voice Mode</div></button>
                        <div className="pt-4 pb-2">
                            <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">Healing Aids {subscriptionState.hasAccess && <Sparkles size={10} className="text-yellow-500" />}</p>
                            <button onClick={() => handleNav(AppView.BOTANICAL, true)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center justify-between transition-colors ${currentView === AppView.BOTANICAL ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><div className="flex items-center gap-3"><Leaf size={18} className="text-blue-500" /> Botanical Rx</div>{!subscriptionState.hasAccess && <Lock size={12} className="text-gray-400" />}</button>
                            <button onClick={() => handleNav(AppView.YOGA, true)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center justify-between transition-colors ${currentView === AppView.YOGA ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><div className="flex items-center gap-3"><Flower2 size={18} className="text-pink-500" /> Yoga Aid</div>{!subscriptionState.hasAccess && <Lock size={12} className="text-gray-400" />}</button>
                            <button onClick={() => handleNav(AppView.DIET, true)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center justify-between transition-colors ${currentView === AppView.DIET ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><div className="flex items-center gap-3"><Utensils size={18} className="text-orange-500" /> Nutri Heal</div>{!subscriptionState.hasAccess && <Lock size={12} className="text-gray-400" />}</button>
                            <button onClick={() => handleNav(AppView.WISDOM, true)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center justify-between transition-colors ${currentView === AppView.WISDOM ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><div className="flex items-center gap-3"><Brain size={18} className="text-indigo-500" /> Manas Aid</div>{!subscriptionState.hasAccess && <Lock size={12} className="text-gray-400" />}</button>
                        </div>
                        <button onClick={() => handleNav(AppView.LIBRARY, true)} className={`w-full text-left px-4 py-2 rounded-lg font-medium flex items-center justify-between transition-colors ${currentView === AppView.LIBRARY ? 'bg-sage-100 text-sage-800' : 'text-gray-600 hover:bg-gray-50'}`}><div className="flex items-center gap-3"><BookMarked size={18} className="text-blue-500" /> Saved Library</div>{!subscriptionState.hasAccess && <Lock size={12} className="text-gray-400" />}</button>

                        <div className="pt-6 pb-2 space-y-6">
                            {/* The Nani's Corner */}
                            <div>
                                <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">The Nani's Corner</p>
                                <Link to="/about" className="block w-full text-left px-4 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-sage-700 hover:bg-gray-50 transition-colors">About NatureNani</Link>
                                <Link to="/faq" className="block w-full text-left px-4 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-sage-700 hover:bg-gray-50 transition-colors">FAQ</Link>
                            </div>

                            {/* Terms and Policy */}
                            <div>
                                <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Terms and Policy</p>
                                <Link to="/disclaimer" className="block w-full text-left px-4 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-sage-700 hover:bg-gray-50 transition-colors">Disclaimer</Link>
                                <Link to="/privacy" className="block w-full text-left px-4 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-sage-700 hover:bg-gray-50 transition-colors">Privacy Policy</Link>
                                <Link to="/terms" className="block w-full text-left px-4 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-sage-700 hover:bg-gray-50 transition-colors">Terms of Service</Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-sage-100">
                    {user ? (
                        <div className="px-2">
                            <button onClick={() => handleNav(AppView.ACCOUNT)} className="w-full flex items-center gap-3 p-2 mb-2 bg-sage-50/50 rounded-xl hover:bg-sage-100 transition-all text-left group">
                                <div className="w-8 h-8 rounded-full bg-sage-600 flex items-center justify-center text-white font-bold text-xs shadow-sm group-hover:scale-105 transition-transform">
                                    {user.name?.[0] || user.email[0].toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold text-sage-900 truncate">{user.name || 'User'}</p>
                                </div>
                            </button>
                            <button 
                                onClick={() => handleNav(AppView.PROFILE)} 
                                className={`flex items-center gap-2 transition-all text-xs px-3 w-full py-2.5 mb-1 rounded-xl font-medium ${
                                    currentView === AppView.PROFILE 
                                        ? 'bg-emerald-100/80 text-emerald-900 border border-emerald-200/50' 
                                        : 'text-[#2D5A27]/70 hover:bg-emerald-50 hover:text-emerald-800'
                                }`}
                            >
                                <HeartPulse size={14} className={currentView === AppView.PROFILE ? 'text-emerald-700' : ''} /> 
                                My Wellness Profile
                            </button>
                            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors text-xs px-2 w-full py-2"><LogOut size={14} /> Sign Out</button>
                        </div>
                    ) : (
                        <button onClick={() => setShowAuthModal(true)} className="w-full bg-sage-600 text-white py-2 rounded-lg font-bold text-sm hover:bg-sage-700 transition-colors shadow-lg">Sign In</button>
                    )}
                </div>
            </div>

            <div className="flex-1 h-full relative overflow-hidden flex flex-col">
                {/* Persistent Onboarding Banner */}
                {showOnboardingBanner && (
                    <div className="z-40 shrink-0">
                        {/* Desktop Banner (Original) */}
                        <div className="hidden md:flex bg-sage-600 text-white px-4 py-3 items-center justify-between shadow-md">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <span className="text-lg">🌿</span>
                                <span>Get personalized guidance! Complete your Body Wisdom Journey</span>
                            </div>
                            <button
                                onClick={() => navigate('/onboarding')}
                                className="bg-white text-sage-700 px-4 py-1.5 rounded-full text-xs font-bold hover:bg-sage-50 transition-colors shadow-sm"
                            >
                                Take the Wellness Assessment
                            </button>
                        </div>

                        {/* Mobile Sticky Progress Bar (New) */}
                        <button
                            onClick={() => navigate('/onboarding')}
                            className="md:hidden w-full bg-sage-50 border-b border-sage-200 h-10 flex items-center relative overflow-hidden group"
                        >
                            {/* Progress Bar Visual (Simulated ~70% or generic progress) */}
                            <div className="absolute top-0 left-0 bottom-0 w-[85%] bg-gradient-to-r from-emerald-500 to-teal-400 opacity-20 group-hover:opacity-30 transition-opacity"></div>

                            <div className="w-full flex items-center justify-between px-4 relative z-10">
                                <span className="text-xs font-bold text-sage-800 flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                    Take the Wellness Assessment
                                </span>
                                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 bg-white/50 px-2 py-0.5 rounded-sm">
                                    Resume
                                </span>
                            </div>
                        </button>
                    </div>
                )}

                {/* Vikriti Nudge Banner */}
                {showVikritiNudge && (
                    <div className="z-40 shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between shadow-sm animate-in slide-in-from-top duration-500">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                                <TargetIcon size={18} />
                            </div>
                            <div className="flex flex-col">
                                <p className="text-sm font-bold text-amber-900">Get a more complete remedy</p>
                                <p className="text-xs text-amber-700">Nani has your constitutional profile, but knowing your current state of balance significantly improves the accuracy of your recommendations.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => {
                                    setShowVikritiNudge(false);
                                    navigate('/onboarding', { state: { mode: 'vikriti-refresh' } });
                                }}
                                className="bg-amber-600 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-amber-700 transition-colors shadow-sm"
                            >
                                Add Concern
                            </button>
                            <button 
                                onClick={() => setShowVikritiNudge(false)}
                                className="p-1.5 text-amber-400 hover:text-amber-600 transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                <div className={`flex-1 w-full ${currentView === AppView.CHAT ? 'block' : 'hidden'} overflow-hidden`}>
                    <ChatInterface
                        messages={chatMessages}
                        setMessages={setChatMessages}
                        onTrialEnd={() => setShowPaywall(true)}
                        hasAccess={subscriptionState.hasAccess}
                        subscriptionStatus={subscriptionState.status}
                        initialMessage={triggerQuery}
                        onMessageSent={() => {
                            if (user) refreshAppData(user);
                            setTriggerQuery('');
                            setIsVoiceTrigger(false);
                        }}
                        usage={queryUsage}
                        onUpgradeClick={() => setShowPaywall(true)}
                        isGuest={!user}
                        onShowAuth={() => setShowAuthModal(true)}
                        onNavigateToFeature={(v, c) => handleFeatureHandoff(v, c, true)}
                        onVoiceClick={() => handleNav(AppView.VOICE, false)}
                        initialMessageIsVoice={isVoiceTrigger}
                        isProfileActive={!!(userProfile && (userProfile.age || userProfile.sex || userProfile.health_data?.medical_history))}
                        userProfile={userProfile}
                        onConsultationResponse={(response, ailmentId) => {
                            setActiveConsultationResponse(response);
                            setActiveAilmentId(ailmentId);
                        }}
                        onResetChat={() => {
                            mealPlanCache.current.clear();
                            yogaProtocolCache.current.clear();
                            setActiveConsultationResponse(null);
                            setActiveAilmentId(null);
                        }}
                    />
                </div>
                {currentView === AppView.ACCOUNT && user && (
                    <AccountSettings user={user} onUpgrade={() => setShowPaywall(true)} onLogout={handleLogout} onRefresh={() => refreshAppData(user)} />
                )}
                {currentView === AppView.LIBRARY && user && <Library user={user} onNavigate={(v, c) => handleFeatureHandoff(v, c, false)} />}
                {currentView === AppView.PROFILE && <WellnessPassport onBack={() => {
                    if (user) refreshAppData(user);
                    setCurrentView(AppView.CHAT);
                }} />}
                {currentView === AppView.BOTANICAL && <BotanicalRx activeContext={featureContext} onBack={featureContext ? () => setCurrentView(returnToChat ? AppView.CHAT : AppView.LIBRARY) : undefined} />}
                {currentView === AppView.YOGA && (
                    <YogaAid 
                        activeContext={featureContext} 
                        userProfile={userProfile} 
                        onBack={featureContext ? () => setCurrentView(returnToChat ? AppView.CHAT : AppView.LIBRARY) : undefined}
                        consultationResponse={activeConsultationResponse}
                        cache={yogaProtocolCache}
                    />
                )}
                {currentView === AppView.DIET && (
                    <NutriHeal 
                        activeContext={featureContext} 
                        userProfile={userProfile} 
                        onBack={featureContext ? () => setCurrentView(returnToChat ? AppView.CHAT : AppView.LIBRARY) : undefined}
                        consultationResponse={activeConsultationResponse}
                        cache={mealPlanCache}
                    />
                )}
                {currentView === AppView.WISDOM && (
                    <ManasAid 
                        onBack={featureContext ? () => setCurrentView(returnToChat ? AppView.CHAT : AppView.LIBRARY) : undefined}
                        activeContext={featureContext || undefined}
                    />
                )}
                {currentView === AppView.LEGAL && <LegalNotice onBack={() => setCurrentView(AppView.CHAT)} />}
                {currentView === AppView.ABOUT && <AboutView onBack={() => setCurrentView(AppView.CHAT)} />}
                {currentView === AppView.FAQ && <FAQView onBack={() => setCurrentView(AppView.CHAT)} />}
                {currentView === AppView.VOICE && <VoiceConsultation onClose={() => setCurrentView(AppView.CHAT)} onSubmit={handleVoiceConsult} />}
            </div>

            <AuthForm isOpen={showAuthModal} onAuthSuccess={(u) => handleAuthChange(u)} onClose={() => setShowAuthModal(false)} onNavigate={(view) => handleNav(view)} />
            <SubscriptionModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} isTrialExpired={subscriptionState.isTrialExpired} daysRemaining={subscriptionState.daysRemaining} subscriptionStatus={subscriptionState.status} onRefreshUser={() => user && refreshAppData(user)} />
        </div>
    );
};

export default ConsultationApp;
